import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { environment } from 'src/environments/environment';
import { IPrescription } from './iprescription';
import { PaginationRequest } from 'src/app/models/common.request';

@Injectable({
  providedIn: 'root'
})
export class PrescriptionService {

  private _prescription: BehaviorSubject<IPrescription | null> = new BehaviorSubject<IPrescription | null>(null);
  private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);
  private _prescriptions: BehaviorSubject<IPrescription[] | null> = new BehaviorSubject<IPrescription[] | null>(null);


  constructor(private _httpClient: HttpClient) { }

  get Prescription$(): Observable<IPrescription | null> {
    return this._prescription.asObservable();
  }

  get pagination$(): Observable<PaginationResult | null> {
    return this._pagination.asObservable();
  }

  get prescriptions$(): Observable<IPrescription[] | null> {
    return this._prescriptions.asObservable();
  }


  createPrescription(prescriptiondata: IPrescription): Observable<BaseRespone<IPrescription>> {
    return this._httpClient.post<BaseRespone<IPrescription>>(`${environment.baseURl}/Prescription`, prescriptiondata).pipe(
      tap((response) => {
        this._prescription.next(response.result);
      }),
      catchError((error) => {
        console.error('Error creating Prescription:', error);
        throw error;
      })
    );
  }

  getAllPrescription(prescriptiondata: PaginationRequest): Observable<BaseRespone<{ pagination: PaginationResult; prescriptionDetails: IPrescription[] }>> {
    return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; prescriptionDetails: IPrescription[] }>>(`${environment.baseURl}/Prescription/List`, prescriptiondata).pipe(
      tap((response) => {
        this._pagination.next(response.result.pagination);
        this._prescriptions.next(response.result.prescriptionDetails);
      }),
      catchError((error) => {
        console.error('Error fetching data:', error);
        throw error;
      })
    );
  }

  deletePrescription(id: number): Observable<BaseRespone<boolean>> {
    return this.prescriptions$.pipe(
      take(1),
      switchMap(prescription =>
        this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseURl}/Prescription/${id}`).pipe(
          map((deletedResponse) => {
            if (deletedResponse.result) {

              const updated = (prescription ?? []).filter(item => item.id !== id);
              this._prescriptions.next(updated);
            }
            return deletedResponse;
          })
        )
      ), catchError((error) => {
        console.error('Error to Fetching data:', error);
        throw error;
      })
    );
  }

  updatePrescription(prescriptiondetails: IPrescription): Observable<BaseRespone<IPrescription>> {
    return this.prescriptions$.pipe(
      take(1),
      switchMap(prescriptions => this._httpClient.put<BaseRespone<IPrescription>>(`${environment.baseURl}/Prescription`, prescriptiondetails)
        .pipe(
          tap((updatedResponse) => {
            const list = prescriptions ?? [];
            const updatedList = list.map(item =>
              item.id === prescriptiondetails.id ? prescriptiondetails : item
            );
            this._prescriptions.next(updatedList);
          })
        )
      ),
      catchError(error => {
        console.error('Error updating Prescription:', error);
        throw error;
      })
    );
  }

  getPrescriptionDetailByOpdId(opdId: number) {
    return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/Prescription/${opdId}`).pipe(
      tap((response) => {
        this._prescription.next(response.result);
      }),
      catchError((error) => {
        return throwError(() => error.error);
      })
    );
  }

  getPrescriptionDetailByIPDId(ipdId: number) {
    return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/Prescription/ipd/${ipdId}`).pipe(
      tap((response) => {
        this._prescription.next(response.result);
      }),
      catchError((error) => {
        return throwError(() => error.error);
      })
    );
  }
}

